import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Check } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { KeyValueEditor } from '@/components/ui/KeyValueEditor'
import { PricingMatrix } from './PricingMatrix'
import {
  updatePlan,
  getPlanPrices,
  createPlanPrice,
  updatePlanPrice,
  deletePlanPrice,
  getFeatureDefinitions,
  groupFeatureDefinitions,
  type PlanListItem,
  type PlanPrice,
  type PlanPriceCreate,
  type FeatureGroup,
} from '@/lib/api'

interface PlanEditDrawerProps {
  plan: PlanListItem | null
  serviceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button type="button" variant="ghost" size="sm" onClick={handleCopy} className="gap-2">
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}

export function PlanEditDrawer({
  plan,
  serviceId,
  open,
  onOpenChange,
}: PlanEditDrawerProps) {
  const queryClient = useQueryClient()

  // Form state
  const [formData, setFormData] = React.useState<Partial<PlanListItem>>({})
  const [prices, setPrices] = React.useState<PlanPrice[]>([])
  const [pendingPriceCreates, setPendingPriceCreates] = React.useState<PlanPriceCreate[]>([])
  const [pendingPriceDeletes, setPendingPriceDeletes] = React.useState<string[]>([])
  const [isSaving, setIsSaving] = React.useState(false)

  // Load plan prices
  const { data: loadedPrices } = useQuery({
    queryKey: ['plan-prices', serviceId, plan?.id],
    queryFn: () => getPlanPrices(serviceId, plan!.id),
    enabled: !!plan && open,
  })

  // Load feature definitions for grouped checkboxes
  const { data: featureDefinitions = [] } = useQuery({
    queryKey: ['feature-definitions', serviceId],
    queryFn: () => getFeatureDefinitions(serviceId),
    enabled: open,
  })

  // Group feature definitions
  const featureGroups: FeatureGroup[] = React.useMemo(() => {
    return groupFeatureDefinitions(featureDefinitions)
  }, [featureDefinitions])

  // Initialize form data when plan changes
  React.useEffect(() => {
    if (plan) {
      setFormData({
        code: plan.code,
        name: plan.name,
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        currency: plan.currency,
        limits: plan.limits,
        features: plan.features,
        is_active: plan.is_active,
        is_recommended: plan.is_recommended,
      })
    }
  }, [plan])

  // Initialize prices when loaded
  React.useEffect(() => {
    if (loadedPrices) {
      setPrices(loadedPrices)
    }
  }, [loadedPrices])

  // Reset pending changes when drawer opens/closes
  React.useEffect(() => {
    if (open) {
      setPendingPriceCreates([])
      setPendingPriceDeletes([])
    }
  }, [open])

  // Mutations
  const updatePlanMutation = useMutation({
    mutationFn: (data: Partial<PlanListItem>) =>
      updatePlan(serviceId, plan!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service', serviceId] })
    },
  })

  const handleInputChange = (field: keyof PlanListItem, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleFeatureToggle = (featureCode: string, enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...(prev.features || {}),
        [featureCode]: enabled,
      },
    }))
  }

  const handlePriceCreate = (priceData: PlanPriceCreate) => {
    setPendingPriceCreates(prev => [...prev, priceData])
    // Add a temporary price to the local state for UI
    const tempPrice: PlanPrice = {
      id: `temp-${Date.now()}-${priceData.interval}`,
      plan_id: plan!.id,
      currency: priceData.currency,
      interval: priceData.interval,
      amount: priceData.amount,
      stripe_price_id: null,
      is_active: priceData.is_active ?? true,
    }
    setPrices(prev => [...prev, tempPrice])
  }

  const handlePriceDelete = (priceId: string) => {
    if (priceId.startsWith('temp-')) {
      // Remove from pending creates
      setPrices(prev => prev.filter(p => p.id !== priceId))
      setPendingPriceCreates(prev => {
        const price = prices.find(p => p.id === priceId)
        return prev.filter(p => !(p.currency === price?.currency && p.interval === price?.interval))
      })
    } else {
      // Add to pending deletes
      setPendingPriceDeletes(prev => [...prev, priceId])
      setPrices(prev => prev.filter(p => p.id !== priceId))
    }
  }

  const handleSave = async () => {
    if (!plan) return

    setIsSaving(true)
    try {
      // 1. Update plan basic info
      await updatePlanMutation.mutateAsync(formData)

      // 2. Update existing prices
      for (const price of prices) {
        if (!price.id.startsWith('temp-')) {
          const original = loadedPrices?.find(p => p.id === price.id)
          if (original && (original.amount !== price.amount || original.is_active !== price.is_active)) {
            await updatePlanPrice(serviceId, plan.id, price.id, {
              amount: price.amount,
              is_active: price.is_active,
            })
          }
        }
      }

      // 3. Create new prices
      for (const priceData of pendingPriceCreates) {
        await createPlanPrice(serviceId, plan.id, priceData)
      }

      // 4. Delete prices
      for (const priceId of pendingPriceDeletes) {
        await deletePlanPrice(serviceId, plan.id, priceId)
      }

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['service', serviceId] })
      queryClient.invalidateQueries({ queryKey: ['plan-prices', serviceId, plan.id] })

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save plan:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (!plan) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-3">
            Edit Plan: {plan.name}
            {plan.is_recommended && (
              <Badge variant="default" className="bg-yellow-500">
                Recommended
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Edit plan settings, pricing, limits, and features.
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="basic" className="mt-4">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="limits">Limits</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="stripe">Stripe</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="grid gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Code</label>
                <Input
                  value={formData.code || ''}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  placeholder="starter"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Starter"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Monthly Price ({formData.currency})</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_monthly || ''}
                    onChange={(e) => handleInputChange('price_monthly', e.target.value)}
                    placeholder="9.00"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Yearly Price ({formData.currency})</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_yearly || ''}
                    onChange={(e) => handleInputChange('price_yearly', e.target.value)}
                    placeholder="90.00"
                  />
                </div>
              </div>

              <div className="flex gap-6 pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active ?? true}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span className="text-sm font-medium">Active</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_recommended ?? false}
                    onChange={(e) => handleInputChange('is_recommended', e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span className="text-sm font-medium">Recommended</span>
                </label>
              </div>
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="pt-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Configure multi-currency pricing. Add prices in different currencies for
                international customers.
              </p>
              <PricingMatrix
                prices={prices}
                onChange={setPrices}
                onPriceCreate={handlePriceCreate}
                onPriceDelete={handlePriceDelete}
              />
            </div>
          </TabsContent>

          {/* Limits Tab */}
          <TabsContent value="limits" className="pt-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Define numeric limits for this plan (e.g., employees: 10, locations: 1).
              </p>
              <KeyValueEditor
                data={formData.limits || {}}
                onChange={(limits) => handleInputChange('limits', limits)}
                valueType="number"
                keyPlaceholder="Limit name"
                valuePlaceholder="Value"
              />
            </div>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="pt-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Toggle features on/off for this plan. Features are defined in the Features tab of the service.
              </p>

              {featureGroups.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
                  No features defined yet. Add features in the Features tab.
                </div>
              ) : (
                <div className="space-y-4">
                  {featureGroups.map((group) => (
                    <div key={group.group_code} className="rounded-lg border">
                      <div className="border-b bg-muted/50 px-4 py-2">
                        <span className="font-medium">{group.group_label_key}</span>
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({group.features.length})
                        </span>
                      </div>
                      <div className="divide-y">
                        {group.features.map((feature) => (
                          <label
                            key={feature.id}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.features?.[feature.code] === true}
                              onChange={(e) => handleFeatureToggle(feature.code, e.target.checked)}
                              className="h-4 w-4 rounded border-input"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{feature.feature_label_key}</div>
                              <code className="text-xs text-muted-foreground">{feature.code}</code>
                            </div>
                            {feature.is_highlight && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                Highlight
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Stripe Tab */}
          <TabsContent value="stripe" className="pt-4">
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Stripe product and price IDs are read-only. To change them, create new
                products/prices in Stripe and update via API.
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Stripe Product ID</label>
                  <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3 font-mono text-sm">
                    <span className="flex-1 truncate">
                      {plan.stripe_product_id || 'Not configured'}
                    </span>
                    {plan.stripe_product_id && <CopyButton text={plan.stripe_product_id} />}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Stripe Price Monthly ID</label>
                  <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3 font-mono text-sm">
                    <span className="flex-1 truncate">
                      {plan.stripe_price_monthly_id || 'Not configured'}
                    </span>
                    {plan.stripe_price_monthly_id && <CopyButton text={plan.stripe_price_monthly_id} />}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Stripe Price Yearly ID</label>
                  <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3 font-mono text-sm">
                    <span className="flex-1 truncate">
                      {plan.stripe_price_yearly_id || 'Not configured'}
                    </span>
                    {plan.stripe_price_yearly_id && <CopyButton text={plan.stripe_price_yearly_id} />}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <SheetFooter className="mt-6 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
